import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import {
  Assignment as TemplateIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  Launch as LaunchIcon,
  Search as SearchIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { useWebSocket } from './WebSocketContext';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';
import {
  clearCaseCreation,
  startCaseCreation,
} from '../Utils/caseCreationState';

const primaryColor = '#5757b0';
const primaryColorTransparent = 'rgba(87, 87, 176, 0.8)';
const hoverColor = '#7777d0';
const lightPurple = '#f0f0ff';

const TemplateSelectionDialog = ({ open, onClose, onSelectTemplate }) => {
  const { t } = useTranslation();
  const { sendMessage, subscribeToMessages, isConnected } = useWebSocket();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTitleInput, setShowTitleInput] = useState(false);
  const [projectTitle, setProjectTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(false);
  const [importFeedback, setImportFeedback] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [pendingImportData, setPendingImportData] = useState(null);

  const handleClose = useCallback(() => {
    setSelectedTemplate(null);
    setError(null);
    setShowTitleInput(false);
    setProjectTitle('');
    setCreating(false);
    setSearchTerm('');
    setImporting(false);
    setImportFeedback(null);
    setDuplicateDialogOpen(false);
    setPendingImportData(null);
    clearCaseCreation();
    onClose();
  }, [onClose]);

  // Load templates when dialog opens
  const loadTemplates = useCallback(() => {
    setLoading(true);
    setError(null);
    sendMessage({
      command: 'getAllTemplates'
    });
  }, [sendMessage]);

  const handleImportTemplate = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const fileData = JSON.parse(e.target.result);
          if (fileData.templates && Array.isArray(fileData.templates) && fileData.templates.length > 1) {
            setPendingImportData({ type: 'multiple', data: fileData.templates });
          } else {
            const templateData = fileData.templates ? fileData.templates[0] : fileData;
            setPendingImportData({ type: 'single', data: templateData });
          }
          setDuplicateDialogOpen(true);
          setImportFeedback(null);
          setError(null);
        } catch {
          setImportFeedback({
            severity: 'error',
            message: t('template.invalid_json_file', 'Invalid JSON file format')
          });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleConfirmedImport = (skipDuplicates) => {
    if (!pendingImportData) return;

    setDuplicateDialogOpen(false);
    setImporting(true);
    setImportFeedback(null);
    setError(null);

    if (pendingImportData.type === 'multiple') {
      sendMessage({
        command: 'importTemplates',
        data: {
          templates_data: pendingImportData.data,
          skip_duplicates: skipDuplicates
        }
      });
    } else {
      sendMessage({
        command: 'importTemplate',
        data: {
          template_data: pendingImportData.data,
          skip_duplicates: skipDuplicates
        }
      });
    }
  };

  const handleCancelledImport = () => {
    setDuplicateDialogOpen(false);
    setPendingImportData(null);
    setImporting(false);
  };

  // Filter templates based on search term
  const filteredTemplates = useMemo(() => {
    if (!searchTerm.trim()) return templates;
    
    const searchLower = searchTerm.toLowerCase();
    return templates.filter(template => {
      const title = (template.template_title || template.name || '').toLowerCase();
      const description = (template.template_description || template.description || '').toLowerCase();
      const projectDesc = (template.project_description || '').toLowerCase();
      
      return title.includes(searchLower) || 
             description.includes(searchLower) || 
             projectDesc.includes(searchLower);
    });
  }, [templates, searchTerm]);

  useEffect(() => {
    if (open) {
      loadTemplates();
      // Reset search when dialog opens
      setSearchTerm('');
    }
  }, [open, loadTemplates]);

  // Handle WebSocket messages
  useEffect(() => {
    const messageHandler = (message) => {
      const data = message.data || {};
      
      switch (message.command) {
        case 'getAllTemplates':
          setLoading(false);
          if (data.status === 'success') {
            const templates = data.templates || [];
            setTemplates(templates);
          } else {
            const errorMessage = data.message || 'Failed to load templates';
            setError(errorMessage);
          }
          break;
        case 'loadTemplate':
          if (data.status === 'success') {
            const template = data.template;
            onSelectTemplate(template);
            handleClose();
          } else {
            const errorMessage = data.message || 'Failed to load template';
            setError(errorMessage);
          }
          break;
        case 'createProjectFromTemplate':
          setCreating(false);
          if (data.status === 'success') {
            // Project created successfully, navigate to it
            const projectId = data.project_id;
            clearCaseCreation();
            if (projectId && window.location) {
              window.location.href = `/case/${projectId}`;
            }
            handleClose();
          } else {
            clearCaseCreation();
            const errorMessage = data.message || 'Failed to create project from template';
            setError(errorMessage);
          }
          break;
        case 'importTemplate':
          setImporting(false);
          setPendingImportData(null);
          if (data.status === 'success') {
            setImportFeedback({
              severity: 'success',
              message: data.message || t('template.template_imported', 'Template imported successfully')
            });
            loadTemplates();
          } else if (data.status === 'skipped') {
            setImportFeedback({
              severity: 'info',
              message: data.message || t('template.template_skipped', 'Template already exists - import skipped')
            });
          } else {
            setImportFeedback({
              severity: 'error',
              message: data.message || t('template.template_import_error', 'Failed to import template')
            });
          }
          break;
        case 'importTemplates': {
          setImporting(false);
          setPendingImportData(null);

          if (data.status === 'success' || data.status === 'partial_success') {
            const importedCount = Number(data.imported_count || 0);
            const skippedCount = Number(data.skipped_count || 0);
            const failedCount = Number(data.failed_count || 0);
            const feedbackMessage =
              data.message ||
              t('template.bulk_import_result', {
                defaultValue: 'Imported {{imported}} templates. Skipped: {{skipped}}. Failed: {{failed}}.',
                imported: importedCount,
                skipped: skippedCount,
                failed: failedCount
              });
            setImportFeedback({
              severity: data.status === 'partial_success' ? 'warning' : 'success',
              message: feedbackMessage
            });

            if (importedCount > 0) {
              loadTemplates();
            }
          } else if (data.status === 'skipped') {
            setImportFeedback({
              severity: 'info',
              message: data.message || t('template.bulk_import_skipped', 'All templates already exist - nothing imported')
            });
          } else {
            setImportFeedback({
              severity: 'error',
              message: data.message || t('template.bulk_import_error', 'Failed to import templates')
            });
          }
          break;
        }
        default:
          // Ignore other commands
          break;
      }
    };

    const unsubscribe = subscribeToMessages(messageHandler);
    return unsubscribe;
  }, [subscribeToMessages, onSelectTemplate, handleClose, loadTemplates, t]);

  const handleSelectTemplate = () => {
    if (selectedTemplate) {
      // Show title input instead of loading template
      setShowTitleInput(true);
      setProjectTitle(selectedTemplate.template_title || selectedTemplate.name || '');
    }
  };

  const handleCreateProject = () => {
    if (selectedTemplate && projectTitle.trim()) {
      if (!sendMessage || !isConnected) {
        clearCaseCreation();
        setError(t('addcase.datasource_upload_error', 'Unable to create the project right now. Please try again.'));
        return;
      }
      setCreating(true);
      setError(null);
      startCaseCreation();
      sendMessage({
        command: 'createProjectFromTemplate',
        data: {
          template_id: selectedTemplate.id,
          title: projectTitle.trim()
        }
      });
    }
  };

  const handleBackToTemplates = () => {
    setShowTitleInput(false);
    setProjectTitle('');
    setSelectedTemplate(null);
  };

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 12px 28px rgba(87, 87, 176, 0.15)',
          border: `1px solid ${lightPurple}`,
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: lightPurple,
          color: primaryColor,
          fontWeight: 'bold',
          borderBottom: `1px solid ${lightPurple}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TemplateIcon sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="h6" component="div">
            {t('template.select_template')}
          </Typography>
        </Box>
        <CloseButton
          onClick={handleClose}
          sx={{
            color: primaryColor,
            '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.1)' }
          }}
        />
      </DialogTitle>

      <DialogContent sx={{ 
        pt: 4, 
        px: 3, 
        pb: 3, 
        maxHeight: '70vh', 
        overflowY: 'auto',
        '&.MuiDialogContent-root': {
          paddingTop: '32px !important'
        }
      }}>
        {importFeedback && (
          <Alert
            severity={importFeedback.severity || 'info'}
            sx={{ mb: 2 }}
            onClose={() => setImportFeedback(null)}
          >
            {importFeedback.message}
          </Alert>
        )}

        {showTitleInput ? (
          // Show project title input
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: primaryColor }}>
              {t('template.create_project_from_template', { templateName: selectedTemplate?.template_title || selectedTemplate?.name })}
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              {t('template.enter_project_title')}
            </Typography>

            <TextField
              fullWidth
              label={t('template.project_title')}
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter' && projectTitle.trim()) {
                  handleCreateProject();
                }
              }}
            />

            {creating && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                <CircularProgress sx={{ color: primaryColor, mr: 2 }} size={20} />
                <Typography sx={{ color: 'text.secondary' }}>
                  {t('template.creating_project')}
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        ) : (
          // Show template selection
          <>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress sx={{ color: primaryColor }} />
                <Typography sx={{ ml: 2, color: 'text.secondary' }}>
                  {t('template.loading_templates')}
                </Typography>
              </Box>
            )}

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button
                    size="small"
                    onClick={loadTemplates}
                    sx={{ color: 'error.main' }}
                  >
                    {t('template.retry')}
                  </Button>
                }
              >
                {error}
              </Alert>
            )}

            {!loading && !error && templates.length === 0 && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  color: 'text.secondary'
                }}
              >
                <TemplateIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('template.no_templates_found')}
                </Typography>
                <Typography variant="body2">
                  {t('template.create_template_first')}
                </Typography>
              </Box>
            )}

            {!loading && templates.length > 0 && filteredTemplates.length === 0 && searchTerm.trim() && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  color: 'text.secondary'
                }}
              >
                <TemplateIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('template.no_search_results')}
                </Typography>
                <Typography variant="body2">
                  {t('template.try_different_search')}
                </Typography>
                <Button
                  variant="text"
                  onClick={() => setSearchTerm('')}
                  sx={{ mt: 2, color: primaryColor }}
                >
                  {t('template.clear_search')}
                </Button>
              </Box>
            )}

            {!loading && templates.length > 0 && (
              <>
                <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                  {t('template.select_template_description')}
                </Typography>

                {/* Search Input */}
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t('template.search_templates')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                  sx={{ 
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'rgba(250, 250, 255, 0.5)',
                      '&:hover fieldset': {
                        borderColor: primaryColor
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, color: 'text.secondary' }}>
                        <SearchIcon fontSize="small" />
                      </Box>
                    ),
                    endAdornment: searchTerm.trim() && (
                      <CloseButton
                        size="small"
                        onClick={() => setSearchTerm('')}
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { color: primaryColor }
                        }}
                      />
                    )
                  }}
                />

                {/* Template Results Count */}
                {searchTerm.trim() && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 2, 
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      transition: 'opacity 0.3s ease',
                      animation: 'fadeIn 0.5s ease'
                    }}
                  >
                    {t('template.showing_results', { 
                      count: filteredTemplates.length,
                      total: templates.length
                    })}
                  </Typography>
                )}

                <Grid container spacing={2}>
                  {filteredTemplates.map((template) => (
                <Grid item xs={12} sm={6} md={4} key={template.id}>
                  <Card
                    onClick={() => handleTemplateClick(template)}
                    sx={{
                      cursor: 'pointer',
                      border: selectedTemplate?.id === template.id 
                        ? `2px solid ${primaryColor}` 
                        : '1px solid rgba(0, 0, 0, 0.12)',
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      backgroundColor: selectedTemplate?.id === template.id 
                        ? lightPurple 
                        : 'white',
                      '&:hover': {
                        borderColor: primaryColor,
                        boxShadow: '0 4px 16px rgba(87, 87, 176, 0.15)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <TemplateIcon 
                          sx={{ 
                            color: primaryColor, 
                            mr: 1, 
                            mt: 0.5,
                            fontSize: 20 
                          }} 
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 'bold',
                              color: primaryColor,
                              fontSize: '1rem',
                              lineHeight: 1.3,
                              mb: 1
                            }}
                          >
                            {template.name}
                          </Typography>
                          
                          {template.version && (
                            <Chip
                              label={`v${template.version}`}
                              size="small"
                              sx={{
                                backgroundColor: primaryColorTransparent,
                                color: 'white',
                                fontSize: '0.7rem',
                                height: '20px'
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      {template.description && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <DescriptionIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {t('template.description')}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.8rem',
                              lineHeight: 1.4,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {template.description}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ my: 1 }} />

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5 }} />
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {formatDate(template.created_at)}
                          </Typography>
                        </Box>
                        
                        {selectedTemplate?.id === template.id && (
                          <LaunchIcon 
                            sx={{ 
                              fontSize: 16, 
                              color: primaryColor,
                              animation: 'pulse 1.5s infinite'
                            }} 
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
        </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 1,
          backgroundColor: 'rgba(250, 250, 255, 0.5)',
          borderTop: `1px solid ${lightPurple}`
        }}
      >
        {showTitleInput ? (
          <>
            <Button 
              onClick={handleBackToTemplates} 
              sx={{ 
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              {t('template.back')}
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={!projectTitle.trim() || creating}
              variant="contained"
              sx={{
                backgroundColor: primaryColor,
                '&:hover': { backgroundColor: hoverColor },
                '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' },
                borderRadius: '8px',
                px: 3
              }}
            >
              {creating ? t('template.creating') : t('template.create_project')}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleImportTemplate}
              disabled={loading || creating || importing}
              startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
              sx={{
                color: primaryColor,
                borderColor: 'rgba(87, 87, 176, 0.3)',
                '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.08)' }
              }}
              variant="outlined"
            >
              {importing ? t('template.importing', 'Importing...') : t('template.import', 'Import')}
            </Button>
            <Button 
              onClick={handleClose} 
              sx={{ 
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              {t('template.cancel')}
            </Button>
            <Button
              onClick={handleSelectTemplate}
              disabled={!selectedTemplate}
              variant="contained"
              sx={{
                backgroundColor: primaryColor,
                '&:hover': { backgroundColor: hoverColor },
                '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' },
                borderRadius: '8px',
                px: 3
              }}
            >
              {t('template.use_template')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>

    <Dialog
      open={duplicateDialogOpen}
      onClose={handleCancelledImport}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 12px 28px rgba(87, 87, 176, 0.15)'
        }
      }}
    >
      <DialogTitle sx={{ color: primaryColor, fontWeight: 'bold' }}>
        {t('template.import_options', 'Import Options')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {pendingImportData?.type === 'multiple'
            ? t('template.import_multiple_message', {
                defaultValue: 'You are importing {{count}} templates. How should duplicates be handled?',
                count: pendingImportData?.data?.length || 0
              })
            : t('template.import_single_message', 'You are importing a template. How should duplicates be handled?')}
        </Typography>
        <Alert severity="info" sx={{ mb: 1 }}>
          {t(
            'template.skip_duplicates_description',
            'Skip duplicates is safer and prevents overwriting existing template names.'
          )}
        </Alert>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCancelledImport} sx={{ color: 'text.secondary' }}>
          {t('template.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={() => handleConfirmedImport(false)}
          variant="outlined"
          sx={{ borderColor: '#d18b00', color: '#d18b00' }}
        >
          {t('template.import_all_allow_duplicates', 'Import All')}
        </Button>
        <Button
          onClick={() => handleConfirmedImport(true)}
          variant="contained"
          sx={{ backgroundColor: primaryColor, '&:hover': { backgroundColor: hoverColor } }}
        >
          {t('template.skip_duplicates', 'Skip Duplicates')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default TemplateSelectionDialog;
